import { useQuery } from '@tanstack/react-query'
import { Bell, Plus, AlertTriangle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiClient } from '@/lib/api-client'
import type { Alert, AlertChannel, AlertPolicy } from '@/types'

export function AlertingPage() {
  const { data: alerts } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => apiClient.get<Alert[]>('/alerts'),
  })

  const { data: channels } = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => apiClient.get<AlertChannel[]>('/alert-channels'),
  })

  const { data: _policies } = useQuery({
    queryKey: ['alert-policies'],
    queryFn: () => apiClient.get<AlertPolicy[]>('/alert-policies'),
  })
  void _policies

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Alerting</h1>
          <p className="text-muted-foreground">
            Manage alert channels, policies, and view history
          </p>
        </div>
      </div>

      <Tabs defaultValue="alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts">Active Alerts</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          <div className="space-y-4">
            {(alerts?.data || []).length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
                  <CardTitle className="mb-2">No active alerts</CardTitle>
                  <CardDescription>All systems are operating normally</CardDescription>
                </CardContent>
              </Card>
            ) : (
              (alerts?.data || []).map((alert) => (
                <Card key={alert.id}>
                  <CardHeader className="flex flex-row items-start justify-between space-y-0">
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className={`h-5 w-5 mt-0.5 ${
                          alert.severity === 'critical' ? 'text-destructive' :
                          alert.severity === 'warning' ? 'text-warning' : 'text-muted-foreground'
                        }`}
                      />
                      <div>
                        <CardTitle className="text-base">{alert.message}</CardTitle>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          {new Date(alert.triggeredAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <Badge variant={
                      alert.status === 'active' ? 'destructive' :
                      alert.status === 'acknowledged' ? 'warning' : 'secondary'
                    }>
                      {alert.status}
                    </Badge>
                  </CardHeader>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="channels">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Alert Channels</h2>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Channel
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(channels?.data || []).length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Bell className="h-12 w-12 text-muted-foreground mb-4" />
                  <CardTitle className="mb-2">No channels configured</CardTitle>
                  <CardDescription>Add a channel to receive alerts</CardDescription>
                </CardContent>
              </Card>
            ) : (
              (channels?.data || []).map((channel) => (
                <Card key={channel.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{channel.name}</CardTitle>
                      <Badge variant={channel.enabled ? 'success' : 'secondary'}>
                        {channel.enabled ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <CardDescription>{channel.type}</CardDescription>
                  </CardHeader>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="policies">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Alert Policies</h2>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Policy
            </Button>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CardTitle className="mb-2">No policies configured</CardTitle>
              <CardDescription>Create a policy to define alert rules</CardDescription>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Alert History</CardTitle>
              <CardDescription>View past alerts and their resolutions</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">No alert history available</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
